import React, { useState, useEffect } from 'react';
import { useRouteMatch, useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { connectToStore } from '../../../store';
import Highlights from '../../../shared/highlights/Highlights';
import Button from '../../../shared/button/Button';
import Toolbar from '../../../layout/Toolbar';
import { PlusIcon, TableIcon, RefreshIcon } from '../../../shared/icons';
import Table from '../../../shared/table/Table';
import { AgGridColumn } from 'ag-grid-react';
import styles from './ClientList.module.scss';

const ID_FIELD_KEY = `enterprise_id`;

function constructHighlights(state) {
	return [
		{
			colour: `#07BAB4`,
			value: state.clientList.rowData.length,
			label: `Clients`,
		},
		{
			colour: `#07BAB4`,
			value: state.clientList.rowData.filter(row => row.subscription_status === `ACTIVE`).length,
			label: `Active Subscriptions`,
		},
		{
			colour: `#07BAB4`,
			value: state.clientList.rowData.filter(row => row.subscription_status === `USED`).length,
			label: `Used Subscriptions`,
		},
		{
			colour: `#07BAB4`,
			value: state.clientList.rowData.filter(row => row.subscription_status === `EXPIRED`).length,
			label: `Expired Subscriptions`,
		},
	];
}

function navigateToItem(history, url, evt) {
	history.push(`${url}/details/${evt.data[ID_FIELD_KEY]}`);
}

function navigateToSubscription(history, evt) {
	if (evt.data.subscription_id) history.push(`/subscriptions/details/${evt.data.subscription_id}`);
}

function DataView({
	loading,
	success,
	errorMessage,
	actions,
	rowData,
	tableState,
	setTableCtrl,
	navigateToItemBound,
	navigateToSubscriptionBound,
}) {
	if (!loading && !success) {
		return (
			<div className={styles.errorView}>
				<span>Error: {errorMessage || `An unknown error ocurred`}</span>
			</div>
		);
	}

	return (
		<Table
			loading={loading}
			rowData={rowData}
			tableState={tableState}
			onStateChange={tableState => actions.clientList.updateTableState(tableState)}
			onTableReady={tableCtrl => setTableCtrl(tableCtrl)}
		>
			<AgGridColumn type="id" field={ID_FIELD_KEY} onCellDoubleClicked={navigateToItemBound}></AgGridColumn>
			<AgGridColumn
				type="navigation"
				headerName="Name"
				field="enterprise_name"
				tooltipField="enterprise_name"
				minWidth={200}
				flex={1}
				onCellDoubleClicked={navigateToItemBound}
			></AgGridColumn>
			<AgGridColumn
				type="subscriptionStatus"
				headerName="Subscription"
				field="subscription_status"
				width={140}
				onCellDoubleClicked={navigateToSubscriptionBound}
			></AgGridColumn>
			<AgGridColumn type="number" headerName="Employee Sign Ups" field="num_employees" width={180}></AgGridColumn>
			<AgGridColumn type="number" headerName="Logins" field="num_logins" width={100}></AgGridColumn>
			<AgGridColumn type="number" headerName="Sign Ups (last 30 days)" field="sign_up_30" width={200}></AgGridColumn>
			<AgGridColumn
				type="number"
				headerName="Sessions (last 30 days)"
				field="sessions_held_30"
				width={200}
			></AgGridColumn>
			<AgGridColumn
				type="url"
				headerName="Dashboard Link"
				field="subdomain_label"
				sortable={false}
				filter={false}
				valueFormatter={data => `https://${data.value}.lettheinsideout.com`}
				minWidth={300}
				flex={1}
			></AgGridColumn>
			<AgGridColumn type="datetime" headerName="Created" field="created" sort="desc"></AgGridColumn>
		</Table>
	);
}

function ClientList({ state, actions, className }) {
	const { tableState, loading, success, errorMessage, rowData } = state.clientList;
	const highlights = constructHighlights(state);
	const history = useHistory();
	const [tableCtrl, setTableCtrl] = useState(undefined);
	const { url } = useRouteMatch();
	const navigateToItemBound = navigateToItem.bind(undefined, history, url);
	const navigateToSubscriptionBound = navigateToSubscription.bind(undefined, history);
	const cls = classNames({
		[styles.clientList]: true,
		[className]: !!className,
	});
	useEffect(() => actions.clientList.loadData(), [actions.clientList]);

	return (
		<div className={cls}>
			<Highlights highlights={highlights} />
			<Toolbar>
				<Button to={`${url}/details/new`}>
					<PlusIcon />
					<span>Add Client</span>
				</Button>
				<Button onClick={() => actions.clientList.loadData()} disabled={loading}>
					<RefreshIcon />
					<span>Refresh</span>
				</Button>
				<Button onClick={() => tableCtrl && tableCtrl.reset()} disabled={loading || !success}>
					<TableIcon />
					<span>Reset Table</span>
				</Button>
			</Toolbar>
			<DataView
				loading={loading}
				success={success}
				errorMessage={errorMessage}
				actions={actions}
				rowData={rowData}
				tableState={tableState}
				setTableCtrl={setTableCtrl}
				navigateToItemBound={navigateToItemBound}
				navigateToSubscriptionBound={navigateToSubscriptionBound}
			/>
		</div>
	);
}

export default connectToStore(ClientList);
